import React, { useState, useEffect, useRef, useCallback } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import { track } from "../services/events";

// ── Mega-menu data ─────────────────────────────────────────────────────────

interface NavItem {
  to: string;
  label: string;
  desc?: string;
  icon?: string;
  badge?: string;
}

interface NavColumn {
  heading: string;
  items: NavItem[];
}

interface MegaPanel {
  id: string;
  label: string;
  columns: NavColumn[];
  featured: {
    to: string;
    label: string;
    desc: string;
    cta: string;
    bg: string;
  };
}

const MEGA_PANELS: MegaPanel[] = [
  {
    id: "tools",
    label: "Tools",
    featured: {
      to: "/tools",
      label: "All Tools →",
      desc: "12+ free and pro tools across finance, analytics, design, development, and productivity.",
      cta: "Browse library",
      bg: "from-indigo-600 to-purple-700",
    },
    columns: [
      {
        heading: "Finance",
        items: [
          { to: "/tools/budget-planner",          icon: "💰", label: "Budget Planner",      desc: "Zero-based, envelopes & net worth" },
          { to: "/tools/portfolio-risk-analyzer",  icon: "📊", label: "Portfolio Risk",      desc: "Monte Carlo simulation" },
          { to: "/tools/loan-calculator",          icon: "🏦", label: "Loan Calculator",     desc: "EMI & amortization" },
          { to: "/tools/mortgage-comparison",      icon: "🏠", label: "Mortgage Comparison", desc: "Compare up to 5 scenarios" },
          { to: "/tools/roi-calculator",           icon: "📈", label: "ROI Calculator",      desc: "Return on investment" },
        ],
      },
      {
        heading: "Analytics & Dev",
        items: [
          { to: "/tools/seo-audit",       icon: "🔍", label: "SEO Audit",       desc: "On-page analysis" },
          { to: "/tools/keyword-density", icon: "📝", label: "Keyword Density", desc: "Content keyword analysis" },
          { to: "/tools/json-formatter",  icon: "🗂️", label: "JSON Formatter",  desc: "Format & validate JSON" },
          { to: "/tools/regex-tester",    icon: "⚡", label: "Regex Tester",    desc: "Test regular expressions" },
          { to: "/tools/hash-generator",  icon: "🔒", label: "Hash Generator",  desc: "MD5, SHA-256 & more" },
        ],
      },
      {
        heading: "Design & Content",
        items: [
          { to: "/tools/color-palette",      icon: "🎨", label: "Color Palette",    desc: "Generate harmonious palettes" },
          { to: "/tools/font-pairing",       icon: "🔤", label: "Font Pairing",     desc: "Google Fonts combinations" },
          { to: "/tools/qr-generator",       icon: "📱", label: "QR Generator",     desc: "Create QR codes instantly" },
          { to: "/tools/word-counter",       icon: "📄", label: "Word Counter",     desc: "Words, chars & reading time" },
          { to: "/tools/document-summarizer",icon: "📋", label: "Doc Summarizer",   desc: "AI-powered summaries", badge: "AI" },
        ],
      },
    ],
  },
  {
    id: "services",
    label: "Services",
    featured: {
      to: "/consulting",
      label: "Start a Project →",
      desc: "Assess · Build · Scale — a proven engagement model for digital transformation.",
      cta: "View all services",
      bg: "from-slate-800 to-slate-900",
    },
    columns: [
      {
        heading: "Consulting",
        items: [
          { to: "/consulting",            icon: "🎯", label: "Overview",           desc: "How we engage" },
          { to: "/consulting#expertise",  icon: "🏗️", label: "Areas of Expertise", desc: "Cloud, CMS, Frontend, Analytics" },
          { to: "/outcomes",              icon: "📈", label: "Client Outcomes",    desc: "Qualitative case studies" },
          { to: "/pricing",               icon: "💳", label: "Pricing",            desc: "Free, Pro & Enterprise" },
        ],
      },
      {
        heading: "AI Services",
        items: [
          { to: "/ai",                    icon: "🤖", label: "AI Services",         desc: "Strategy to production AI",   badge: "New" },
          { to: "/radar",                 icon: "📡", label: "Technology Radar",    desc: "Our quarterly tech guide",    badge: "New" },
          { to: "/consulting#expertise",  icon: "⚙️", label: "Automation",         desc: "Workflows & process AI" },
          { to: "/consulting#expertise",  icon: "☁️", label: "Cloud & Edge",       desc: "Cloudflare, AWS, GCP" },
        ],
      },
      {
        heading: "Expertise",
        items: [
          { to: "/consulting#expertise",  icon: "🖥️", label: "Frontend Applications", desc: "React, Next.js, Vite" },
          { to: "/consulting#expertise",  icon: "📝", label: "Content Management",    desc: "AEM, WordPress, Headless" },
          { to: "/consulting#expertise",  icon: "🔗", label: "Atlassian Suite",        desc: "Jira, Confluence, Bitbucket" },
          { to: "/consulting#expertise",  icon: "📊", label: "Google Analytics",       desc: "GA4, GTM, BigQuery" },
        ],
      },
    ],
  },
  {
    id: "insights",
    label: "AI & Insights",
    featured: {
      to: "/radar",
      label: "View April 2026 Radar →",
      desc: "Our quarterly guide: what to adopt, trial, assess, and hold across AI, Cloud, Frontend & Automation.",
      cta: "Explore Radar",
      bg: "from-purple-700 to-indigo-800",
    },
    columns: [
      {
        heading: "AI Hub",
        items: [
          { to: "/insights",     icon: "🧭", label: "Insights Hub",          desc: "All trends and guides",       badge: "New" },
          { to: "/radar",        icon: "📡", label: "Technology Radar",      desc: "April 2026 edition" },
          { to: "/ai-readiness", icon: "🧠", label: "AI Readiness Guide",    desc: "Assess before you scale" },
          { to: "/ai",           icon: "🤖", label: "AI Services",           desc: "Strategy to production AI" },
        ],
      },
      {
        heading: "Perspectives",
        items: [
          { to: "/newsroom",  icon: "📰", label: "Newsroom",          desc: "Company updates & launches" },
          { to: "/playbooks", icon: "⚛️", label: "Frontend Playbooks", desc: "Delivery patterns that scale" },
          { to: "/playbooks", icon: "⚙️", label: "Automation Playbooks", desc: "Execution with guardrails" },
          { to: "/outcomes",  icon: "📈", label: "Client Outcomes",   desc: "Real-world impact stories" },
        ],
      },
      {
        heading: "Guides & Resources",
        items: [
          { to: "/templates",  icon: "🧰", label: "Templates Library",   desc: "Reusable execution assets" },
          { to: "/academy",    icon: "🎓", label: "Learning Center",     desc: "Team enablement modules" },
          { to: "/consulting", icon: "🗺️", label: "Engagement Model",    desc: "Assess → Build → Scale" },
          { to: "/partners",   icon: "🤝", label: "Technology Partners", desc: "Cloudflare, Stripe, React" },
          { to: "/trust",      icon: "🔒", label: "Security & Privacy",   desc: "How we handle your data" },
        ],
      },
    ],
  },
  {
    id: "company",
    label: "Company",
    featured: {
      to: "/about",
      label: "Our Story →",
      desc: "A new kind of digital services firm — tools-first, transparent, and built for the modern web.",
      cta: "About 9nd.org",
      bg: "from-emerald-600 to-teal-700",
    },
    columns: [
      {
        heading: "About",
        items: [
          { to: "/about",    icon: "🏢", label: "About Us",        desc: "Mission, values & team" },
          { to: "/partners", icon: "🤝", label: "Partners",        desc: "Our technology ecosystem" },
          { to: "/outcomes", icon: "📈", label: "Client Outcomes", desc: "Impact & case studies" },
          { to: "/newsroom", icon: "📰", label: "Newsroom",        desc: "Latest updates & launches" },
        ],
      },
      {
        heading: "Trust & Legal",
        items: [
          { to: "/trust",      icon: "🔒", label: "Security & Trust", desc: "Data handling & compliance" },
          { to: "/trust",      icon: "🛡️", label: "Privacy Policy",   desc: "How we protect your data" },
          { to: "/pricing",    icon: "💳", label: "Pricing",           desc: "Simple, transparent plans" },
          { to: "/consulting", icon: "📩", label: "Contact Us",        desc: "Start a conversation" },
        ],
      },
    ],
  },
];

// ── Component ──────────────────────────────────────────────────────────────

export default function Navbar() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activePanel, setActivePanel] = useState<string | null>(null);
  const [mobileExpanded, setMobileExpanded] = useState<string | null>(null);
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const navRef = useRef<HTMLDivElement>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setActivePanel(null);
    setMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!menuOpen) {
      document.body.style.overflow = "";
      return;
    }

    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, [menuOpen]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (navRef.current && !navRef.current.contains(e.target as Node)) {
        setActivePanel(null);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const openPanel = useCallback((id: string) => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setActivePanel(id);
  }, []);

  const closePanel = useCallback(() => {
    closeTimer.current = setTimeout(() => setActivePanel(null), 120);
  }, []);

  const cancelClose = useCallback(() => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
  }, []);

  const isActive = (path: string) => location.pathname === path;

  return (
    <nav
      ref={navRef}
      className={`sticky top-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-white/95 backdrop-blur-md shadow-md" : "bg-white shadow-sm"
      }`}
    >
      <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-[84px] items-center">

          {/* ── Logo ── */}
          <Link to="/" className="flex items-center gap-3 hover:opacity-90 transition shrink-0">
            <img
              src="/assets/brand/logo-mark.png"
              alt="9nd.org"
              className="w-14 h-14 object-contain lg:hidden"
              loading="eager"
              decoding="async"
            />
            <div className="hidden lg:flex items-center gap-3">
              <img
                src="/assets/brand/logo-mark.png"
                alt="9nd.org"
                className="w-14 h-14 lg:w-16 lg:h-16 object-contain"
                loading="eager"
                decoding="async"
              />
              <div className="leading-none">
                <div className="text-[1.9rem] lg:text-[2.15rem] font-black tracking-[-0.06em] text-slate-900">
                  9nd<span className="text-indigo-600">.org</span>
                </div>
                <div className="mt-1 text-[0.64rem] lg:text-[0.7rem] font-semibold tracking-[0.42em] text-slate-500 uppercase">
                  Dimensional Insights
                </div>
              </div>
            </div>
          </Link>

          {/* ── Desktop mega-menu triggers ── */}
          <div className="hidden lg:flex items-center space-x-0.5">
            {MEGA_PANELS.map(panel => (
              <div
                key={panel.id}
                onMouseEnter={() => openPanel(panel.id)}
                onMouseLeave={closePanel}
                className="relative"
              >
                <button
                  onClick={() => setActivePanel(activePanel === panel.id ? null : panel.id)}
                  className={`flex items-center gap-1 px-3.5 py-2.5 rounded-lg text-sm font-semibold transition-colors ${
                    activePanel === panel.id
                      ? "bg-indigo-50 text-indigo-600"
                      : "text-gray-600 hover:text-indigo-600 hover:bg-indigo-50"
                  }`}
                >
                  {panel.label}
                  <svg
                    className={`w-3.5 h-3.5 transition-transform duration-200 ${activePanel === panel.id ? "rotate-180" : ""}`}
                    fill="none" stroke="currentColor" viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
              </div>
            ))}
            {user?.role === "admin" && (
              <Link to="/admin" className="px-3 py-2 rounded-lg text-sm font-semibold text-gray-600 hover:text-indigo-600 hover:bg-indigo-50">
                Admin
              </Link>
            )}
          </div>

          {/* ── Desktop auth ── */}
          <div className="hidden lg:flex items-center gap-3">
            {user ? (
              <>
                <Link to="/dashboard" className="text-sm font-medium text-gray-600 hover:text-indigo-600">Dashboard</Link>
                <Link to="/billing"   className="text-sm font-medium text-gray-600 hover:text-indigo-600">Billing</Link>
                <Link to="/settings"  className="text-sm font-medium text-gray-600 hover:text-indigo-600">Settings</Link>
                <span className="text-xs text-gray-400 border-l pl-3">{user.name}</span>
                <button onClick={handleLogout} className="text-xs text-gray-400 hover:text-red-500 transition">Logout</button>
              </>
            ) : (
              <>
                <Link to="/login" className="text-sm font-medium text-gray-600 hover:text-indigo-600 px-3 py-2 rounded-lg hover:bg-indigo-50 transition">
                  Login
                </Link>
                <Link
                  to="/register"
                  className="text-sm font-bold px-5 py-2 rounded-lg bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-md hover:shadow-lg hover:opacity-90 transition"
                >
                  Get Started Free
                </Link>
              </>
            )}
          </div>

          {/* ── Mobile hamburger ── */}
          <button
            className="lg:hidden p-2 rounded-lg text-gray-600 hover:bg-gray-100 transition"
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label="Toggle menu"
          >
            {menuOpen ? (
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            ) : (
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            )}
          </button>
        </div>
      </div>

      {/* ── Mega-menu panel (desktop) ── */}
      {activePanel && (() => {
        const panel = MEGA_PANELS.find(p => p.id === activePanel);
        if (!panel) return null;
        return (
          <div
            onMouseEnter={cancelClose}
            onMouseLeave={closePanel}
            className="absolute left-0 right-0 top-full z-40 border-t border-gray-200 shadow-2xl bg-white"
          >
            <div className="max-w-screen-2xl mx-auto px-8 py-8">
              <div className="flex gap-8">
                {/* Columns */}
                <div
                  className={`flex-1 grid gap-8 ${
                    panel.columns.length === 3
                      ? "grid-cols-3"
                      : panel.columns.length === 2
                      ? "grid-cols-2"
                      : "grid-cols-1"
                  }`}
                >
                  {panel.columns.map(col => (
                    <div key={col.heading}>
                      <p className="text-[10px] font-black uppercase tracking-[0.18em] text-gray-400 mb-3 pb-2 border-b border-gray-100">
                        {col.heading}
                      </p>
                      <ul className="space-y-1">
                        {col.items.map(item => (
                          <li key={item.to + item.label}>
                            <Link
                              to={item.to}
                              onClick={() => setActivePanel(null)}
                              className="flex items-start gap-3 px-2 py-2 rounded-lg hover:bg-indigo-50 transition group"
                            >
                              {item.icon && (
                                <span className="text-xl shrink-0 mt-0.5">{item.icon}</span>
                              )}
                              <div className="min-w-0">
                                <div className="flex items-center gap-2">
                                  <span className="text-sm font-semibold text-gray-800 group-hover:text-indigo-600 transition">
                                    {item.label}
                                  </span>
                                  {item.badge && (
                                    <span className="text-[9px] font-black uppercase tracking-wider bg-indigo-600 text-white rounded-full px-1.5 py-0.5">
                                      {item.badge}
                                    </span>
                                  )}
                                </div>
                                {item.desc && (
                                  <p className="text-xs text-gray-500 leading-relaxed">{item.desc}</p>
                                )}
                              </div>
                            </Link>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>

                {/* Featured card */}
                {panel.featured && (
                  <div className="w-56 shrink-0">
                    <Link
                      to={panel.featured.to}
                      onClick={() => {
                        track("mega_menu_featured_click", {
                          panel: panel.id,
                          destination: panel.featured.to,
                          label: panel.featured.label,
                          surface: "desktop",
                        });
                        setActivePanel(null);
                      }}
                      className={`block bg-gradient-to-br ${panel.featured.bg} text-white rounded-2xl p-5 h-full hover:opacity-95 transition shadow-lg group`}
                    >
                      <p className="text-xs font-black uppercase tracking-wider text-white/60 mb-3">{panel.featured.cta}</p>
                      <h3 className="font-black text-lg leading-snug mb-3 group-hover:underline">
                        {panel.featured.label}
                      </h3>
                      <p className="text-sm text-white/80 leading-relaxed">{panel.featured.desc}</p>
                    </Link>
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── Mobile menu ── */}
      {menuOpen && (
        <div className="lg:hidden border-t border-gray-100 bg-white max-h-[80vh] overflow-y-auto">
          <div className="px-4 py-4 space-y-1">
            {MEGA_PANELS.map(panel => (
              <div key={panel.id}>
                <button
                  onClick={() => setMobileExpanded(mobileExpanded === panel.id ? null : panel.id)}
                  aria-expanded={mobileExpanded === panel.id}
                  aria-controls={`mobile-panel-${panel.id}`}
                  className="w-full flex items-center justify-between px-3 py-3 rounded-xl text-sm font-bold text-gray-800 hover:bg-indigo-50 transition"
                >
                  <span>{panel.label}</span>
                  <svg
                    className={`w-4 h-4 text-gray-400 transition-transform ${mobileExpanded === panel.id ? "rotate-180" : ""}`}
                    fill="none" stroke="currentColor" viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {mobileExpanded === panel.id && (
                  <div id={`mobile-panel-${panel.id}`} className="ml-3 mt-2 space-y-4 pb-3 pr-1">
                    <Link
                      to={panel.featured.to}
                      onClick={() => {
                        track("mega_menu_featured_click", {
                          panel: panel.id,
                          destination: panel.featured.to,
                          label: panel.featured.label,
                          surface: "mobile",
                        });
                        setMenuOpen(false);
                        setMobileExpanded(null);
                      }}
                      className={`block rounded-xl bg-gradient-to-r ${panel.featured.bg} text-white p-3.5 shadow-sm`}
                    >
                      <p className="text-[10px] uppercase tracking-wider font-bold text-white/70">{panel.featured.cta}</p>
                      <p className="text-sm font-bold mt-1">{panel.featured.label}</p>
                    </Link>
                    {panel.columns.map(col => (
                      <div key={col.heading}>
                        <p className="text-[10px] font-black uppercase tracking-widest text-gray-400 px-3 mb-1.5">{col.heading}</p>
                        {col.items.map(item => (
                          <Link
                            key={item.to + item.label}
                            to={item.to}
                            onClick={() => { setMenuOpen(false); setMobileExpanded(null); }}
                            className="flex items-start gap-2.5 px-3 py-2 rounded-lg text-sm text-gray-700 hover:text-indigo-600 hover:bg-indigo-50 transition"
                          >
                            {item.icon && <span className="text-base">{item.icon}</span>}
                            <span className="min-w-0">
                              <span className="flex items-center gap-1.5">
                                <span className="font-semibold">{item.label}</span>
                                {item.badge && (
                                  <span className="text-[9px] font-black bg-indigo-600 text-white rounded-full px-1.5 py-0.5">{item.badge}</span>
                                )}
                              </span>
                              {item.desc && <span className="block text-xs text-gray-500 mt-0.5">{item.desc}</span>}
                            </span>
                          </Link>
                        ))}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}

            <div className="pt-3 border-t border-gray-100 space-y-1">
              {user ? (
                <>
                  <Link to="/dashboard" className="block px-3 py-2 rounded-lg text-sm text-gray-700 hover:bg-indigo-50" onClick={() => setMenuOpen(false)}>Dashboard</Link>
                  <Link to="/billing"   className="block px-3 py-2 rounded-lg text-sm text-gray-700 hover:bg-indigo-50" onClick={() => setMenuOpen(false)}>Billing</Link>
                  <Link to="/settings"  className="block px-3 py-2 rounded-lg text-sm text-gray-700 hover:bg-indigo-50" onClick={() => setMenuOpen(false)}>Settings</Link>
                  {user.role === "admin" && (
                    <Link to="/admin"   className="block px-3 py-2 rounded-lg text-sm text-gray-700 hover:bg-indigo-50" onClick={() => setMenuOpen(false)}>Admin</Link>
                  )}
                  <button onClick={() => { setMenuOpen(false); handleLogout(); }} className="block w-full text-left px-3 py-2 rounded-lg text-sm text-rose-500 hover:bg-rose-50">
                    Logout
                  </button>
                </>
              ) : (
                <>
                  <Link to="/login"    className="block px-3 py-2 rounded-lg text-sm font-medium text-gray-700 hover:bg-indigo-50" onClick={() => setMenuOpen(false)}>Login</Link>
                  <Link to="/register" className="block px-3 py-2 rounded-lg text-sm font-bold text-white bg-gradient-to-r from-indigo-600 to-purple-600 text-center" onClick={() => setMenuOpen(false)}>
                    Get Started Free
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}
